import type { PostHog } from "posthog-js";
import { type Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

/**
 * Core Web Vitals, measured on real visits and filed in PostHog.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * The launch audit could check every ranking signal on this site except the
 * ones measured on a device. A headless browser in a hidden tab never fires
 * paint timings, and PageSpeed Insights measures one synthetic load from one
 * place — neither tells you what a person on 4G in São Paulo actually got.
 * Google ranks on the field data, so the field data is the only number worth
 * having.
 *
 * ── Why `web-vitals` rather than the PerformanceObserver this used to be ─────
 *
 * The first version of this file was hand-rolled to avoid a dependency, and it
 * measured LCP, CLS, TTFB and FCP correctly. It deliberately did NOT report INP,
 * because INP is the one metric you cannot approximate honestly: it means the
 * worst interaction latency of the visit, measured across the full event group
 * — input delay, processing, and the wait for the next frame — with a
 * documented long-tail rule that ignores one slow interaction per fifty on busy
 * pages. A hand-rolled version reports a number that looks like INP and is not.
 *
 * INP is also the metric this site is most likely to fail. It replaced FID
 * precisely because FID flattered pages that responded to the first tap and
 * then blocked; every `<details>` on this site — the FAQ, the language menu, the
 * article contents — is an interaction, and hydration is where they get slow.
 * Not measuring it is choosing not to know.
 *
 * So: one small dependency (~2 kB gzip, from the Chrome team, the reference
 * implementation the CrUX dataset is defined by), and all five metrics measured
 * the way Google measures them.
 *
 * ── Why it still reports once, at the end ────────────────────────────────────
 *
 * `web-vitals` calls back per metric as each becomes final, which for a naive
 * integration means five PostHog events per page view — five times the billing,
 * and five rows to join before any of it is a chart. These are buffered and
 * sent as ONE event when the page is hidden, which is also the moment INP and
 * CLS stop changing.
 */

/** Google's own boundaries. `web-vitals` reports them; this is the fallback. */
type Rating = "good" | "needs-improvement" | "poor";

export function collectVitals(posthog: PostHog): () => void {
  const metrics: Record<string, number | string> = {};
  let sent = false;

  const record = (metric: Metric) => {
    const name = metric.name.toLowerCase();
    // CLS is a unitless ratio and wants three decimals; every other metric is
    // milliseconds, where a fractional part is noise in a chart.
    metrics[name] =
      metric.name === "CLS"
        ? Number(metric.value.toFixed(3))
        : Math.round(metric.value);
    metrics[`${name}_rating`] = metric.rating as Rating;
  };

  // `reportAllChanges: false` (the default) means each callback fires once, with
  // the value that will actually be reported to CrUX — which is what we want to
  // match. LCP and CLS finalise when the page is hidden; INP finalises then too.
  onLCP(record);
  onCLS(record);
  onINP(record);
  onTTFB(record);
  onFCP(record);

  const flush = () => {
    if (sent || document.visibilityState !== "hidden") return;
    // Nothing measured at all means a browser that supports none of this, and
    // an empty event is worse than no event: it would sit in the same series as
    // the real ones and drag every average toward nothing.
    if (Object.keys(metrics).length === 0) return;
    sent = true;
    posthog.capture("web_vitals", {
      ...metrics,
      path: window.location.pathname,
    });
  };

  // `visibilitychange`, not `unload`: iOS Safari does not reliably fire unload
  // handlers, and a metric that never leaves the phone it was measured on is
  // not a metric. `pagehide` covers the back/forward cache path, where a page
  // is frozen without becoming hidden first.
  document.addEventListener("visibilitychange", flush);
  window.addEventListener("pagehide", flush);

  return () => {
    document.removeEventListener("visibilitychange", flush);
    window.removeEventListener("pagehide", flush);
  };
}
